import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Documents({ customerId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/customer/${customerId}/documents`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data = await response.json();
      setDocuments(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchDocuments();
    }
  }, [customerId]);

  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          const maxDimension = 2000;
          if (width > maxDimension || height > maxDimension) {
            const ratio = Math.min(maxDimension / width, maxDimension / height);
            width *= ratio;
            height *= ratio;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.8;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size <= 5 * 1024 * 1024 || quality <= 0.1) {
                  resolve(blob);
                } else {
                  quality -= 0.1;
                  tryCompress();
                }
              },
              'image/jpeg',
              quality
            );
          };
          tryCompress();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
    });
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type (PDF, DOC, DOCX, TXT, JPG, PNG, WAV)
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg',
      'image/png',
      'audio/wav',
      'audio/x-wav',
      'audio/wave'
    ];

    if (!validTypes.includes(file.type)) {
      setError('Only PDF, Word, TXT, JPG, PNG, and WAV files are supported');
      return;
    }

    // Validate file size (20MB max)
    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      let uploadFile = file;

      // Compress images if over 5MB
      if (['image/jpeg', 'image/png'].includes(file.type) && file.size > 5 * 1024 * 1024) {
        setSuccess('⏳ Compressing image...');
        uploadFile = await compressImage(file);
      }

      const formData = new FormData();
      formData.append('document_file', uploadFile);

      const url = `${BACKEND_URL}/api/customer/${customerId}/upload-document`;
      console.log('Uploading to:', url);

      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });

      console.log('Response status:', response.status, response.statusText);
      const responseText = await response.text();
      console.log('Raw response:', responseText.substring(0, 500));

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        console.error('Response text:', responseText);
        throw new Error('Server returned invalid response: ' + responseText.substring(0, 200));
      }

      if (response.ok) {
        setSuccess('✅ Document uploaded successfully!');
        fetchDocuments();
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError(`❌ Error: ${result.detail || 'Failed to upload'}`);
      }
    } catch (err) {
      setError(`❌ Error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const icons = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      txt: '📋',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️'
    };
    return icons[ext] || '📎';
  };

  const downloadDocument = async (filename, originalName) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/documents/${filename}`
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName || filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error downloading document: ' + err.message);
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/customer/${customerId}/documents/${docId}`,
        { method: 'DELETE' }
      );
      if (!response.ok) throw new Error('Delete failed');
      fetchDocuments();
    } catch (err) {
      alert('Error deleting document: ' + err.message);
    }
  };

  if (loading && documents.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>Loading documents...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ color: '#1b7abf', marginBottom: '10px' }}>📄 Documents</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        Upload and download documents
      </p>

      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '2px dashed #1b7abf',
          borderRadius: '8px',
          padding: '30px',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: '#f9fafb',
          transition: 'all 0.2s',
          marginBottom: '20px'
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eff6ff'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
      >
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📎</div>
        <p style={{ margin: '0 0 8px 0', color: '#1b7abf', fontWeight: 'bold' }}>
          Click to upload or drag & drop
        </p>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
          PDF, Word, TXT, JPG, PNG or WAV (max 20MB)
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.wav"
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={uploading}
      />

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fee2e2',
          border: '1px solid #fca5a5',
          borderRadius: '6px',
          color: '#dc2626',
          marginBottom: '12px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '12px',
          backgroundColor: '#dcfce7',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          color: '#166534',
          marginBottom: '12px'
        }}>
          {success}
        </div>
      )}

      {uploading && (
        <div style={{
          padding: '12px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          color: '#1e40af',
          marginBottom: '12px'
        }}>
          ⏳ Uploading...
        </div>
      )}

      {documents.length === 0 ? (
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #dcfce7',
          borderRadius: '8px',
          padding: '30px',
          textAlign: 'center',
          color: '#166534'
        }}>
          <p style={{ fontSize: '16px', margin: 0 }}>✓ No documents yet</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {documents.map((doc) => (
            <div
              key={doc.id || doc.filename}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
            >
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>{getFileIcon(doc.filename)}</span>
                <div>
                  <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: '#1a202c' }}>
                    {doc.original_name || doc.filename}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                    {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ''}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginLeft: '10px' }}>
                <button
                  onClick={() => downloadDocument(doc.filename, doc.original_name)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#1b7abf',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📥 Download
                </button>
                <button
                  onClick={() => deleteDocument(doc.id)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p style={{ marginTop: '20px', fontSize: '12px', color: '#999', fontStyle: 'italic' }}>
        Powered by Boatman Systems™
      </p>
    </div>
  );
}
